/**
 * The size catalog: the one place that says what the RDS pool tiers and the VMs mean. Mirrors the backend's config/sizing.py, which is
 * authoritative; the Infrastructure flow also reads the backend's copy (`GET /infrastructure/options/`, key `sizing`), and a contract test keeps
 * the two the same.
 *
 * There are four RDS pool tiers and they are the ONLY size the user chooses. A tier sets how many sessions the RDSH pool takes. Every
 * RDS VM is the same, whatever the tier: `Standard_E16ads_v7`, 16 vCPU, 128 GB RAM, Premium SSD. The DB VM is a fixed constant too: today the
 * largest DB size the catalog had (Standard_E16ds_v5), and it is shown as "Recommended size, not confirmed" until it is confirmed.
 *
 * Every page reads from here (the sizes step, the Infrastructure step, the Review step and the admin Platform Config tab), so a number is
 * changed once. Nothing outside this file may spell out a session count, vCPU, memory, SKU or cost.
 */

/** The four pool tiers, by the id the backend and the pipeline know them by. */
export type PoolTierId = 'STD' | 'PREM' | 'HIGH' | 'DED'

export interface PoolTier {
  id: PoolTierId
  /** The full name, as the backend and the pipeline have it: "Premium Collection". */
  label: string
  /** The name without "Collection", for the headline: "Premium". */
  shortLabel: string
  /** How many sessions the RDSH pool takes. */
  sessions: number
}

const tier = (value: PoolTier): Readonly<PoolTier> => Object.freeze(value)

/** The tiers in display order. */
export const POOL_TIERS: readonly Readonly<PoolTier>[] = Object.freeze([
  tier({ id: 'STD', label: 'Std Collection', shortLabel: 'Std', sessions: 120 }),
  tier({ id: 'PREM', label: 'Premium Collection', shortLabel: 'Premium', sessions: 100 }),
  tier({ id: 'HIGH', label: 'High Collection', shortLabel: 'High', sessions: 80 }),
  // "Dedicated Collection" is a pool tier. It is not the Dedicated or Existing choice of a server: the cards say so wherever both appear.
  tier({ id: 'DED', label: 'Dedicated Collection', shortLabel: 'Dedicated', sessions: 70 }),
])

export const POOL_TIER_IDS: readonly PoolTierId[] = Object.freeze(POOL_TIERS.map((entry) => entry.id))

/** The tier a new wizard starts with, and the tier every newly selected product line gets. */
export const DEFAULT_POOL_TIER: PoolTierId = 'STD'

/** What the pool is called on a tier card. */
export const RDS_POOL_LABEL = 'RDSH Pool'

/** What a DB VM cost leaves out. Shown wherever a DB cost is, so the wording is changed here and nowhere else. */
export const DB_COST_LICENSING_NOTE = 'Est. cost excl. SQL licensing'

/** Said wherever the DB VM size is shown: it is a provisional constant, not a confirmed size. */
export const DB_RECOMMENDED_NOTE = 'Recommended size, not confirmed'

/** Said wherever the RDS cost is shown while its price is not set. No price is made up. */
export const RDS_COST_PENDING_NOTE = 'Price to be confirmed'

export const isPoolTierId = (value: unknown): value is PoolTierId => typeof value === 'string' && (POOL_TIER_IDS as readonly string[]).includes(value)

export function getPoolTier(id: PoolTierId): Readonly<PoolTier> {
  return POOL_TIERS.find((entry) => entry.id === id) as Readonly<PoolTier>
}

/** "100 sessions", or "1 session". */
export const sessionsText = (sessions: number): string => `${sessions} session${sessions === 1 ? '' : 's'}`

/** The headline of a tier card: "Premium · 100 sessions". */
export const tierHeadline = (entry: Pick<PoolTier, 'shortLabel' | 'sessions'>): string => `${entry.shortLabel} · ${sessionsText(entry.sessions)}`

// ─── The VMs ─────────────────────────────────────────────────────────────────────────────────────────────────────────────────────────

/** The two kinds of VM a deployment builds: the RDS session hosts and the SQL server VM. */
export type VmKind = 'rds' | 'db'

export interface VmSpec {
  kind: VmKind
  /** The Azure VM size. */
  sku: string
  vcpus: number
  memoryGb: number
  /** What the disks are: Premium SSD. */
  disk: string
  /** The estimated monthly price in USD, or null while it is not set. A price is never invented. */
  estCostUsdPerMonth: number | null
  /** True for a size that is a stand-in until it is confirmed (the DB VM). */
  provisional: boolean
}

const RDS_VM: Readonly<VmSpec> = Object.freeze({
  kind: 'rds',
  sku: 'Standard_E16ads_v7',
  vcpus: 16,
  memoryGb: 128,
  disk: 'Premium SSD',
  estCostUsdPerMonth: null, // PLACEHOLDER: the price of this size has not been supplied.
  provisional: false,
})

const DB_VM: Readonly<VmSpec> = Object.freeze({
  kind: 'db',
  sku: 'Standard_E16ds_v5', // provisional: the largest DB size of the earlier catalog
  vcpus: 16,
  memoryGb: 128,
  disk: 'Premium SSD',
  estCostUsdPerMonth: 1803, // excludes SQL licensing (DB_COST_LICENSING_NOTE)
  provisional: true,
})

/**
 * The one resolver: the VM a tier builds. The RDS VM is the same for every tier, the DB VM too, so `tier` does not change the answer today;
 * it is a parameter so that the day a tier means another VM, this is the only function that changes.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- the tier is part of the contract of the one resolver, though no tier changes a VM today
export function resolveVm(kind: VmKind, _tier?: PoolTierId): Readonly<VmSpec> {
  return kind === 'rds' ? RDS_VM : DB_VM
}

/** "16 vCPU", "128 GB RAM". */
export const vcpuText = (spec: Pick<VmSpec, 'vcpus'>): string => `${spec.vcpus} vCPU`
export const memoryText = (spec: Pick<VmSpec, 'memoryGb'>): string => `${spec.memoryGb} GB RAM`

// ─── How many RDS VMs ────────────────────────────────────────────────────────────────────────────────────────────────────────────────

/**
 * How many RDS VMs a dedicated RDS server request builds: they are created together, in one run, after the whole set has been checked
 * (names, vCPU quota, free IPs in the subnet, VM size). Mirrors the backend's RDS_VM_COUNT_MIN, _MAX and _DEFAULT (config/sizing.py); a
 * contract test keeps the two the same.
 */
export const RDS_VM_COUNT_MIN = 1
export const RDS_VM_COUNT_MAX = 8
export const RDS_VM_COUNT_DEFAULT = 1

/** True for a whole number from RDS_VM_COUNT_MIN to RDS_VM_COUNT_MAX. */
export const isRdsVmCount = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value >= RDS_VM_COUNT_MIN && value <= RDS_VM_COUNT_MAX

/** A whole number within the range: a fraction is cut off, a number outside moves to the nearest end, and what is not a number is the default. */
export function clampRdsVmCount(value: number): number {
  if (!Number.isFinite(value)) return RDS_VM_COUNT_DEFAULT
  return Math.min(RDS_VM_COUNT_MAX, Math.max(RDS_VM_COUNT_MIN, Math.trunc(value)))
}

/** "1 RDS VM", "3 RDS VMs". */
export const rdsVmsText = (count: number): string => `${count} RDS VM${count === 1 ? '' : 's'}`
